import {
  Box,
  BoxProps,
  Button,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"; // Import Box and BoxProps
import { FC, useState } from "react";
import { Organization } from "../../../model";

interface ThemePickerProps extends BoxProps {
  organization: Organization;
  colorType?: "main" | "secondary";
  onUpdate?: (organization: Organization) => void;
}

const OrganizationThemePicker: FC<ThemePickerProps> = ({
  organization,
  onUpdate,
  colorType = "main",
  ...boxProps
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [color, setColor] = useState<string>(
    colorType === "main"
      ? organization?.theme?.palette?.primary?.main ||
          theme.palette.primary.main ||
          "#000000"
      : organization?.theme?.palette?.primary?.dark ||
          theme.palette.primary.dark ||
          "#000000",
  );
  const [activeColor, setActiveColor] = useState<string | undefined>(
    colorType === "main"
      ? organization?.theme?.palette?.primary?.main ||
          theme.palette.primary.main ||
          "#000000"
      : organization?.theme?.palette?.primary?.dark ||
          theme.palette.primary.dark ||
          "#000000",
  );

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleColorChange = (newColor: string) => {
    const isValidColor = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(newColor);
    setColor(newColor);
    if (isValidColor) {
      setActiveColor(newColor);
    } else setActiveColor(undefined);
  };

  const handleSubmit = () => {
    handleClose();
    if (colorType == "main") {
      onUpdate?.({
        ...organization,
        theme: {
          ...organization.theme,
          palette: {
            ...organization.theme?.palette,
            primary: {
              ...organization.theme?.palette?.primary,
              [colorType]:
                activeColor || organization.theme?.palette?.primary?.main,
            },
          },
        },
      });
    } else if (colorType == "secondary") {
      onUpdate?.({
        ...organization,
        theme: {
          ...organization.theme,
          palette: {
            ...organization.theme?.palette,
            primary: {
              ...organization.theme?.palette?.primary,
              dark: activeColor || organization.theme?.palette?.primary?.dark,
            },
          },
        },
      });
    }
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={handleClose}
      >
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            width={"100%"}
            height="75px"
            sx={{
              backgroundColor: activeColor || "#000000",
              borderRadius: 2,
              boxShadow: "inset 0px -6px 12px rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "flex-end",
              padding: "4px",
              p: 1,
            }}
          >
            <Typography variant="body2" color="white">
              <b>Preview</b>
            </Typography>
          </Box>
          <TextField
            label={"Set Color (hex)"}
            value={color.startsWith("#") ? color : `#${color}`} // Ensure the value always starts with #
            onChange={(newValue) => {
              const inputValue = newValue.target.value.replace(/^#/, ""); // Remove any leading #
              handleColorChange(`#${inputValue}`); // Prepend # to the input value
            }}
            inputProps={{
              maxLength: 7, // Limit input length to 7 characters (# + 6 hex digits)
            }}
          />
          <Button variant="contained" onClick={handleSubmit}>
            Select
          </Button>
        </Box>
      </Popover>
      <Box
        {...boxProps}
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          ...boxProps.sx,
        }}
      >
        <Box
          sx={{
            width: "35px",
            height: "35px",
            borderRadius: "50%",
            border: "1px solid #555555",
            backgroundColor: activeColor || "#000000",
          }}
        />
        <Typography variant="body1">
          {colorType === "main" ? "Primary Color" : "Dark Color"}
        </Typography>
      </Box>
    </>
  );
};

export default OrganizationThemePicker;
